import React, { useCallback } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';

import Icon from 'components/generic/Icon.component';
import IP from 'constants/form/inputParser.const';
import useTip from 'hooks/useTip.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import parseBool from 'utils/parsers/parseBool.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './CheckboxInput.module.scss';

const CTAG = 'CheckboxInput';

const SwitchVariant = ({ value, children, isClean, isDirty, isError, isFocused, isValid, tag, disabled, extra, id, label, onBlur, onChange, onClick, onFocus, readOnly, tabIndex }) => (
  <div
    data-bem="CheckboxInput"
    data-tag={tag}
    data-variant="switch"
    className={cn({
      [CN.component]: true,
      [CN.switchVariant]: true,
    })}
  >
    <input
      type="checkbox"
      {...extra}
      id={id}
      className={CN.input}
      data-bem="CheckboxInput__input"
      data-tag={tag}
      data-value={value}
      data-field-clean={isClean}
      data-field-dirty={isDirty}
      data-field-error={isError}
      data-field-focus={isFocused}
      data-field-valid={isValid}
      checked={value}
      readOnly={readOnly}
      disabled={disabled}
      onBlur={onBlur}
      onChange={onChange}
      onClick={onClick}
      onFocus={onFocus}
    />
    <label data-bem="CheckboxInput__label" data-tag={tag} tabIndex={tabIndex} className={CN.label} htmlFor={id}>
      {label}
      {children}
    </label>
  </div>
);

const OptionVariant = ({ value, checked, disabled, children, isClean, isDirty, isError, isFocused, isValid, tag, extra, label, onClick }) => (
  <div
    data-bem="CheckboxInput"
    data-tag={tag}
    data-variant="option"
    className={cn({
      [CN.component]: true,
      [CN.optionVariant]: true,
      [CN.selected]: checked,
      [CN.disabled]: disabled,
    })}
  >
    <div
      {...extra}
      data-bem="CheckboxInput__input"
      data-tag={tag}
      data-value={value}
      data-checked={checked}
      data-field-clean={isClean}
      data-field-dirty={isDirty}
      data-field-error={isError}
      data-field-focus={isFocused}
      data-field-valid={isValid}
      role="button"
      onClick={onClick}
      tabIndex={0}
      className={CN.box}
    >
      {disabled ? <Icon tag={tag} variant="markCircleDisabled" /> : checked ? <Icon tag={tag} variant="markCircle" /> : <Icon tag={tag} variant="markCircleUnselected" />}
    </div>

    <span data-bem="CheckboxInput__label" data-tag={tag} className={CN.label}>
      {label}
      {children}
    </span>
  </div>
);

const TickVariant = ({ value, checked, disabled, children, isClean, isDirty, isError, isFocused, isValid, tag, extra, label, onClick }) => (
  <div
    data-bem="CheckboxInput"
    data-tag={tag}
    data-variant="tick"
    className={cn({
      [CN.component]: true,
      [CN.tickVariant]: true,
      [CN.selected]: checked,
      [CN.disabled]: disabled,
    })}
  >
    <div
      {...extra}
      data-bem="CheckboxInput__input"
      data-tag={tag}
      data-value={value}
      data-checked={checked}
      data-field-clean={isClean}
      data-field-dirty={isDirty}
      data-field-error={isError}
      data-field-focus={isFocused}
      data-field-valid={isValid}
      role="button"
      onClick={onClick}
      tabIndex={0}
      className={CN.box}
    >
      {disabled ? (
        <Icon tag={tag} variant="check" unselected width="lg" /> // TODO: missing styles
      ) : checked ? (
        <Icon tag={tag} variant="check" selected width="lg" />
      ) : (
        <Icon tag={tag} variant="check" unselected width="lg" />
      )}
    </div>

    <span data-bem="CheckboxInput__label" data-tag={tag} className={CN.label}>
      {label}
      {children}
    </span>
  </div>
);

const CheckboxInput = ({
  data,
  disabled,
  checked: checkedFromProps,
  value: valueFromProps,
  name,
  onBlur = noop,
  onChange = noop,
  onClick = noop,
  onFocus = noop,
  readOnly,
  tag: tagFromProps,
  label,
  tabIndex: tabIndexFromProps,
  variant: variantFromProps,
  children,
  ...extra
}) => {
  const tag = fromAnyToTrimmed(tagFromProps);
  const tabIndex = isMissing(tabIndexFromProps) ? 0 : tabIndexFromProps;
  const variant = fromAnyToTrimmed(variantFromProps);

  const isOption = variant === 'option';
  const isTick = variant === 'tick';
  const id = createNodeKey({ tag });

  const { val: localValue, set: setLocalValue } = useTip(isMissing(valueFromProps) ? checkedFromProps : valueFromProps);
  const { val: isDirty, inv: isClean, set: setDirty } = useTip(false);
  const { val: isError, inv: isValid, set: setError } = useTip(false);
  const { val: isFocused, yay: gotFocus, nay: lostFocus } = useTip(false);

  const prepare = useCallback(
    (ev) => {
      const value = isOption ? valueFromProps : fromEventToValue(ev);
      return { ...parseBool({ value }), tag, parser: IP.bool, name, ev, event: ev };
    },
    [isOption, name, valueFromProps, tag],
  );

  const reconcile = useCallback(
    ({ result, prepared }) => {
      const { error, value, dirty } = asObject(result);

      const newValue = isMissing(value) ? prepared.raw : value;
      const newDirty = isMissing(dirty) ? newValue !== valueFromProps : dirty;

      setLocalValue(newValue);
      setDirty(newDirty);
      setError(error);
    },
    [setDirty, setError, setLocalValue, valueFromProps],
  );

  const handleAs = useCallback(
    (name, fn, ev) => {
      const prepared = prepare(ev);
      const result = call(`${CTAG}.${name}`, fn, prepared);
      return reconcile({ result, prepared });
    },
    [prepare, reconcile],
  );

  const handleChange = useCallback((ev) => handleAs('onChange', onChange, ev), [handleAs, onChange]);
  const handleClick = useCallback((ev) => handleAs('onClick', onClick, ev), [handleAs, onClick]);

  const handleFocus = useCallback(
    (ev) => {
      gotFocus();
      return handleAs('onFocus', onFocus, ev);
    },
    [gotFocus, handleAs, onFocus],
  );

  const handleBlur = useCallback(
    (ev) => {
      lostFocus();
      return handleAs('onBlur', onBlur, ev);
    },
    [handleAs, lostFocus, onBlur],
  );

  if (isOption) {
    return (
      <OptionVariant
        checked={checkedFromProps}
        value={valueFromProps}
        tag={tag}
        extra={extra}
        isClean={isClean}
        isDirty={isDirty}
        isError={isError}
        isFocused={isFocused}
        isValid={isValid}
        onClick={handleClick}
        label={label}
        disabled={disabled}
      >
        {children}
      </OptionVariant>
    );
  }

  if (isTick) {
    return (
      <TickVariant
        checked={checkedFromProps}
        value={valueFromProps}
        tag={tag}
        extra={extra}
        isClean={isClean}
        isDirty={isDirty}
        isError={isError}
        isFocused={isFocused}
        isValid={isValid}
        onClick={handleClick}
        label={label}
        disabled={disabled}
      >
        {children}
      </TickVariant>
    );
  }

  return (
    <SwitchVariant
      tag={tag}
      extra={extra}
      id={id}
      isClean={isClean}
      isDirty={isDirty}
      isError={isError}
      isFocused={isFocused}
      isValid={isValid}
      value={localValue}
      readOnly={readOnly}
      disabled={disabled}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      onFocus={handleFocus}
      tabIndex={tabIndex}
      label={label}
    >
      {children}
    </SwitchVariant>
  );
};

// noinspection JSUnusedGlobalSymbols
export default CheckboxInput;
